import React, { useContext, useMemo } from "react";
import { HotelContext } from "../../context/HotelContext";
import { translate } from "../../utils/Translation";
import linkIcon from "../../img/link.png";

const MONETARY_CHART_COLORS = ["#697F6A", "#96B77A", "#B0C996", "#3C5544", "#8FB78F", "#5A7A5A"];
const WATER_CHART_COLORS = ["#1F3451", "#2D4A75", "#6C8EBF", "#8FAED6"];

const formatLegendLabel = (text) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const getTypeBadgeColor = (type) => {
  const colorMap = {
    LUNISHOWER: "#2D4A75",
    LUNICLEAN: "#C68A6A",
    QUIZ: "#3C5544",
    CUSTOM: "#CFA7A0",
    LIMEAT: "#A9A2C3",
    HEATING: "#E7DE9F",
    YESNO: "#6C8EBF",
    YESNOSTAY: "#96B77A",
    REVIEW: "#96B77A",
    CLAIM: "#CFA7A0",
    YESNOMAIL: "#A9A2C3",
    PROOF: "#E7DE9F",
    LINK: "#6C8EBF",
  };
  return colorMap[type?.toUpperCase()] || "#3C5544";
};

export const LoadingIndicator = () => {
  const { lang } = useContext(HotelContext);
  return (
    <div className="card-loading">
      <div className="card-loading__dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <p className="card-loading__text">{translate("v2_loading", lang)}</p>
    </div>
  );
};

export const MonetarySavingsChart = ({ data, formatCurrency }) => {
  const chartData = useMemo(() => {
    if (!data?.details || data.details.length === 0) {
      return { segments: [], allSegments: [], total: data?.total || 0 };
    }

    const total = data.total || 0;
    if (total === 0) {
      return { segments: [], allSegments: [], total: 0 };
    }

    const segmentsWithValue = data.details.filter((detail) => detail.total > 0);
    const totalWithValue = segmentsWithValue.reduce((sum, detail) => sum + detail.total, 0);

    let currentAngle = -90;
    const segments = segmentsWithValue.map((detail, index) => {
      const percentage = (detail.total / totalWithValue) * 100;
      const angle = (percentage / 100) * 360;

      const segment = {
        ...detail,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: MONETARY_CHART_COLORS[index % MONETARY_CHART_COLORS.length],
      };

      currentAngle += angle;
      return segment;
    });

    const legendSegments = data.details.filter((detail) => detail.total > 0);
    const allSegments = legendSegments.map((detail, index) => ({
      ...detail,
      color: MONETARY_CHART_COLORS[index % MONETARY_CHART_COLORS.length],
    }));

    return { segments, allSegments, total };
  }, [data]);

  const formatCurrencyValue = formatCurrency(chartData.total);
  const estimatedTextWidth = formatCurrencyValue.length * 9;
  const minRadiusForText = estimatedTextWidth / 2 + 15;
  const baseSize = 140;
  const thickness = 31;
  const availableRadius = (baseSize - thickness) / 2;
  const size = availableRadius < minRadiusForText ? minRadiusForText * 2 + thickness + 20 : baseSize;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const innerRadius = radius - thickness / 2;
  const maxTextWidth = innerRadius * 1.6;
  const baseFontSize = 18;
  const fontSize = estimatedTextWidth > maxTextWidth ? Math.max(14, baseFontSize * (maxTextWidth / estimatedTextWidth)) : baseFontSize;

  const segmentsWithStyle = useMemo(() => {
    if (chartData.segments.length === 0) return [];

    let orderedSegments = chartData.segments;
    if (orderedSegments.length > 1) {
      const maxIndex = orderedSegments.reduce((bestIdx, s, idx) => {
        if (bestIdx == null) return idx;
        return Number(s.total || 0) > Number(orderedSegments[bestIdx].total || 0) ? idx : bestIdx;
      }, null);
      if (maxIndex != null && maxIndex > 0) {
        orderedSegments = [...orderedSegments.slice(maxIndex), ...orderedSegments.slice(0, maxIndex)];
      }
    }

    let cumulativeOffset = 0;
    return orderedSegments.map((segment) => {
      const percentage = segment.percentage / 100;
      const segmentLength = circumference * percentage;
      const gap = circumference - segmentLength;

      const style = {
        strokeDasharray: `${segmentLength} ${gap}`,
        strokeDashoffset: -cumulativeOffset,
      };

      cumulativeOffset += segmentLength;
      return { ...segment, style };
    });
  }, [chartData.segments, circumference]);

  if (chartData.segments.length === 0) {
    return (
      <div className="monetary-chart">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="monetary-chart__svg">
          <circle cx={center} cy={center} r={radius} fill="none" stroke="#E0E0E0" strokeWidth={thickness} />
          <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="monetary-chart__total" fontSize={fontSize} fontWeight="600" fill="#365644">
            {formatCurrency(chartData.total)}
          </text>
        </svg>

        {chartData.allSegments && chartData.allSegments.length > 0 && (
          <div className="monetary-chart__legend">
            {chartData.allSegments.map((segment, index) => (
              <div key={segment.actId || index} className="monetary-chart__legend-item">
                <div className="monetary-chart__legend-dot" style={{ backgroundColor: segment.color }} />
                <span className="monetary-chart__legend-label">{formatLegendLabel(segment.name || segment.type)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="monetary-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="monetary-chart__svg">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#E0E0E0" strokeWidth={thickness} />

        {segmentsWithStyle
          .slice()
          .reverse()
          .map((segment, index) => (
            <circle
              key={segment.actId || index}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={thickness}
              strokeDasharray={segment.style.strokeDasharray}
              strokeDashoffset={segment.style.strokeDashoffset}
              strokeLinecap="round"
              className="monetary-chart__segment"
              transform={`rotate(-90 ${center} ${center})`}
            />
          ))}

        <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="monetary-chart__total" fontSize={fontSize} fontWeight="600" fill="#365644">
          {formatCurrency(chartData.total)}
        </text>
      </svg>

      {chartData.allSegments.length > 0 && (
        <div className="monetary-chart__legend">
          {chartData.allSegments.map((segment, index) => (
            <div key={segment.actId || index} className="monetary-chart__legend-item">
              <div className="monetary-chart__legend-dot" style={{ backgroundColor: segment.color }} />
              <span className="monetary-chart__legend-label">{formatLegendLabel(segment.name || segment.type)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const WaterSavingsChart = ({ data, formatWater }) => {
  const chartData = useMemo(() => {
    if (!data?.details || data.details.length === 0) {
      return { segments: [], allSegments: [], total: data?.total || 0 };
    }

    const total = data.total || 0;
    if (total === 0) {
      return { segments: [], allSegments: [], total: 0 };
    }

    const segmentsWithValue = data.details.filter((detail) => detail.total > 0);
    const totalWithValue = segmentsWithValue.reduce((sum, detail) => sum + detail.total, 0);

    let currentAngle = -90;
    const segments = segmentsWithValue.map((detail, index) => {
      const percentage = (detail.total / totalWithValue) * 100;
      const angle = (percentage / 100) * 360;

      const segment = {
        ...detail,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: WATER_CHART_COLORS[index % WATER_CHART_COLORS.length],
      };

      currentAngle += angle;
      return segment;
    });

    const legendSegments = data.details.filter((detail) => detail.total > 0);
    const allSegments = legendSegments.map((detail, index) => ({
      ...detail,
      color: WATER_CHART_COLORS[index % WATER_CHART_COLORS.length],
    }));

    return { segments, allSegments, total };
  }, [data]);

  const formatWaterValue = formatWater(chartData.total);
  const estimatedTextWidth = formatWaterValue.length * 9;
  const minRadiusForText = estimatedTextWidth / 2 + 15;
  const baseSize = 140;
  const thickness = 31;
  const availableRadius = (baseSize - thickness) / 2;
  const size = availableRadius < minRadiusForText ? minRadiusForText * 2 + thickness + 20 : baseSize;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const innerRadius = radius - thickness / 2;
  const maxTextWidth = innerRadius * 1.6;
  const baseFontSize = 18;
  const fontSize = estimatedTextWidth > maxTextWidth ? Math.max(14, baseFontSize * (maxTextWidth / estimatedTextWidth)) : baseFontSize;

  const segmentsWithStyle = useMemo(() => {
    if (chartData.segments.length === 0) return [];

    let orderedSegments = chartData.segments;
    if (orderedSegments.length > 1) {
      const maxIndex = orderedSegments.reduce((bestIdx, s, idx) => {
        if (bestIdx == null) return idx;
        return Number(s.total || 0) > Number(orderedSegments[bestIdx].total || 0) ? idx : bestIdx;
      }, null);
      if (maxIndex != null && maxIndex > 0) {
        orderedSegments = [...orderedSegments.slice(maxIndex), ...orderedSegments.slice(0, maxIndex)];
      }
    }

    let cumulativeOffset = 0;
    return orderedSegments.map((segment) => {
      const percentage = segment.percentage / 100;
      const segmentLength = circumference * percentage;
      const gap = circumference - segmentLength;

      const style = {
        strokeDasharray: `${segmentLength} ${gap}`,
        strokeDashoffset: -cumulativeOffset,
      };

      cumulativeOffset += segmentLength;
      return { ...segment, style };
    });
  }, [chartData.segments, circumference]);

  if (chartData.segments.length === 0) {
    return (
      <div className="monetary-chart">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="monetary-chart__svg">
          <circle cx={center} cy={center} r={radius} fill="none" stroke="#E0E0E0" strokeWidth={thickness} />
          <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="monetary-chart__total" fontSize={fontSize} fontWeight="600" fill="#365644">
            {formatWater(chartData.total)}
          </text>
        </svg>

        {chartData.allSegments && chartData.allSegments.length > 0 && (
          <div className="monetary-chart__legend">
            {chartData.allSegments.map((segment, index) => (
              <div key={segment.actId || index} className="monetary-chart__legend-item">
                <div className="monetary-chart__legend-dot" style={{ backgroundColor: segment.color }} />
                <span className="monetary-chart__legend-label">{formatLegendLabel(segment.name || segment.type)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="monetary-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="monetary-chart__svg">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#E0E0E0" strokeWidth={thickness} />

        {segmentsWithStyle
          .slice()
          .reverse()
          .map((segment, index) => (
            <circle
              key={segment.actId || index}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={thickness}
              strokeDasharray={segment.style.strokeDasharray}
              strokeDashoffset={segment.style.strokeDashoffset}
              strokeLinecap="round"
              className="monetary-chart__segment"
              transform={`rotate(-90 ${center} ${center})`}
            />
          ))}

        <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="monetary-chart__total" fontSize={fontSize} fontWeight="600" fill="#365644">
          {formatWater(chartData.total)}
        </text>
      </svg>

      {chartData.allSegments.length > 0 && (
        <div className="monetary-chart__legend">
          {chartData.allSegments.map((segment, index) => (
            <div key={segment.actId || index} className="monetary-chart__legend-item">
              <div className="monetary-chart__legend-dot" style={{ backgroundColor: segment.color }} />
              <span className="monetary-chart__legend-label">{formatLegendLabel(segment.name || segment.type)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const GreenMilesUsageChart = ({ percentage, formatPercentage }) => {
  const { lang } = useContext(HotelContext);
  const value = percentage || 0;
  const formattedValue = formatPercentage(value);

  return (
    <div className="greenmiles-usage-chart">
      <div className="greenmiles-usage-chart__percentage">{formattedValue}</div>
      <div className="greenmiles-usage-chart__indicators">
        <div className="greenmiles-usage-chart__indicator">
          <div className="greenmiles-usage-chart__indicator-box">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 2L6 10M2 6L6 2L10 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>0%</span>
          </div>
          <span className="greenmiles-usage-chart__indicator-label">{translate("v2_vs_last_month", lang)}</span>
        </div>
        <div className="greenmiles-usage-chart__indicator">
          <div className="greenmiles-usage-chart__indicator-box">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 10L6 2M2 6L6 10L10 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>100%</span>
          </div>
          <span className="greenmiles-usage-chart__indicator-label">{translate("v2_vs_last_year", lang)}</span>
        </div>
      </div>
    </div>
  );
};

export const GreenMilesDistributionCard = ({ actions, formatNumber, getActionIcon }) => {
  const { lang } = useContext(HotelContext);

  if (!actions || actions.length === 0) {
    return (
      <div className="actions-list__card">
        <div className="monetary-card__header">
          <h3 className="monetary-card__title">{translate("v2_actions_list_gm_distribution", lang)}</h3>
          <p className="monetary-card__subtitle">{translate("v2_actions_list_by_type", lang)}</p>
        </div>
        <div className="greenmiles-distribution__empty">
          <p>{translate("v2_no_data", lang)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="actions-list__card">
      <div className="monetary-card__header">
        <h3 className="monetary-card__title">{translate("v2_actions_list_gm_distribution", lang)}</h3>
        <p className="monetary-card__subtitle">{translate("v2_actions_list_by_type", lang)}</p>
      </div>
      <div className="greenmiles-distribution__container">
        <div className="greenmiles-distribution__header">
          <div className="greenmiles-distribution__header-cell greenmiles-distribution__header-cell--action" title={translate("v2_actions_list_action_name", lang)}>
            {translate("v2_actions_list_action_name", lang)}
          </div>
          <div className="greenmiles-distribution__header-cell greenmiles-distribution__header-cell--gm" title={translate("v2_actions_list_gm_per_action", lang)}>
            {translate("v2_actions_list_gm_per_action", lang)}
          </div>
        </div>
        <div className="greenmiles-distribution__body">
          {actions.map((action, index) => (
            <div key={action.actId || index} className="greenmiles-distribution__row">
              <div className="greenmiles-distribution__cell greenmiles-distribution__cell-action">
                <div className="greenmiles-distribution__action-icon">{getActionIcon(action.type)}</div>
                <span className="greenmiles-distribution__action-name">{action.name || action.type || "--"}</span>
              </div>
              <div className="greenmiles-distribution__cell greenmiles-distribution__cell--gm">
                <span className="greenmiles-distribution__gm">{formatNumber(Math.round(Number(action.gmParAction || 0)))} GM</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ResourceSavingsCard = ({ actions, formatNumber, getActionIcon }) => {
  const { lang } = useContext(HotelContext);

  if (!actions || actions.length === 0) {
    return (
      <div className="actions-list__card">
        <div className="monetary-card__header">
          <h3 className="monetary-card__title">{translate("v2_actions_list_resource_savings", lang)}</h3>
          <p className="monetary-card__subtitle">{translate("v2_actions_list_pollution", lang)}</p>
        </div>
        <div className="resource-savings__empty">
          <p>{translate("v2_no_data", lang)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="actions-list__card">
      <div className="monetary-card__header">
        <h3 className="monetary-card__title">{translate("v2_actions_list_resource_savings", lang)}</h3>
        <p className="monetary-card__subtitle">{translate("v2_actions_list_pollution", lang)}</p>
      </div>
      <div className="resource-savings__container">
        <div className="resource-savings__header">
          <div className="resource-savings__header-cell" title={translate("v2_actions_list_action_name", lang)}>
            {translate("v2_actions_list_action_name", lang)}
          </div>
          <div className="resource-savings__header-cell resource-savings__header-cell--metric" title={translate("v2_actions_list_co2", lang)}>
            {translate("v2_actions_list_co2", lang)}
          </div>
          <div className="resource-savings__header-cell resource-savings__header-cell--metric" title={translate("v2_actions_list_water_header", lang)}>
            {translate("v2_actions_list_water_header", lang)}
          </div>
          <div className="resource-savings__header-cell resource-savings__header-cell--metric" title={translate("v2_actions_list_energy_header", lang)}>
            {translate("v2_actions_list_energy_header", lang)}
          </div>
        </div>
        <div className="resource-savings__body">
          {actions.map((action, index) => (
            <div key={action.actId || index} className="resource-savings__row">
              <div className="resource-savings__cell resource-savings__cell-action">
                <div className="resource-savings__action-icon">{getActionIcon(action.type)}</div>
                <span className="resource-savings__action-name">{action.name || action.type || "--"}</span>
              </div>
              <div className="resource-savings__cell resource-savings__cell--metric">
                <span className="resource-savings__value-box">{formatNumber(Math.round(Number(action.savingsCO2 || 0)))} kg</span>
              </div>
              <div className="resource-savings__cell resource-savings__cell--metric">
                <span className="resource-savings__value-plain">{formatNumber(Math.round(Number(action.savingsWater || 0)))} L</span>
              </div>
              <div className="resource-savings__cell resource-savings__cell--metric">
                <span className="resource-savings__value-plain resource-savings__value-energy">
                  {formatNumber(Math.round(Number(action.savingsEnergy || 0)))} kW
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const UsageStatisticsCard = ({ actions, formatNumber, formatCurrency, formatPercentage, getActionIcon, onActionNavigate }) => {
  const { lang } = useContext(HotelContext);

  if (!actions || actions.length === 0) {
    return (
      <div className="actions-list__card actions-list__card-large">
        <div className="monetary-card__header">
          <h3 className="monetary-card__title">{translate("v2_actions_list_usage_stats", lang)}</h3>
          <p className="monetary-card__subtitle">{translate("v2_actions_list_usage_trends", lang)}</p>
        </div>
        <div className="usage-statistics__empty">
          <p>{translate("v2_actions_list_no_action_month", lang)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="actions-list__card actions-list__card-large">
      <div className="monetary-card__header">
        <h3 className="monetary-card__title">{translate("v2_actions_list_usage_stats", lang)}</h3>
        <p className="monetary-card__subtitle">{translate("v2_actions_list_usage_trends", lang)}</p>
      </div>
      <div className="usage-statistics__container">
        <div className="usage-statistics__header">
          <div className="usage-statistics__header-cell">{translate("v2_actions_list_usage_action", lang)}</div>
          <div className="usage-statistics__header-cell">{translate("v2_actions_list_usage_type", lang)}</div>
          <div className="usage-statistics__header-cell">{translate("v2_actions_list_usage_total", lang)}</div>
          <div className="usage-statistics__header-cell">{translate("v2_actions_list_usage_time", lang)}</div>
          <div className="usage-statistics__header-cell">{translate("v2_actions_list_usage_opens", lang)}</div>
          <div className="usage-statistics__header-cell">{translate("v2_actions_list_usage_percent", lang)}</div>
          <div className="usage-statistics__header-cell usage-statistics__header-cell--action">{translate("v2_actions_list_usage_action", lang)}</div>
        </div>
        <div className="usage-statistics__body">
          {actions.map((action, index) => (
            <div key={action.id || index} className="usage-statistics__row">
              <div className="usage-statistics__cell usage-statistics__cell-action">
                <button
                  type="button"
                  className="usage-statistics__action-icon-btn"
                  onClick={() => onActionNavigate?.(action)}
                  aria-label={translate("v2_actions_list_nav_aria", lang, {
                    name: action.name || action.type || translate("v2_actions_list_this_action", lang),
                  })}
                >
                  <div className="usage-statistics__action-icon">{getActionIcon(action.type)}</div>
                </button>
                <span className="usage-statistics__action-name">{action.name || "--"}</span>
              </div>
              <div className="usage-statistics__cell">
                <span className="usage-statistics__type-badge" style={{ backgroundColor: getTypeBadgeColor(action.type) }}>
                  {formatLegendLabel(action.type)}
                </span>
              </div>
              <div className="usage-statistics__cell">
                <span className="usage-statistics__total-economie">{formatCurrency(action.totalEconomie)}</span>
              </div>
              <div className="usage-statistics__cell">
                {action.tempsUsage !== null && action.tempsUsage !== undefined ? (
                  <span className="usage-statistics__value-box">{formatNumber(action.tempsUsage)}</span>
                ) : (
                  <span className="usage-statistics__value-empty">--</span>
                )}
              </div>
              <div className="usage-statistics__cell">
                {action.nbOuverture !== null && action.nbOuverture !== undefined ? (
                  <span className="usage-statistics__value-box">{formatNumber(action.nbOuverture)}</span>
                ) : (
                  <span className="usage-statistics__value-empty">--</span>
                )}
              </div>
              <div className="usage-statistics__cell">
                <span className="usage-statistics__percentage">{formatPercentage(action.utilisationPercent)}</span>
              </div>
              <div className="usage-statistics__cell usage-statistics__cell--action">
                <button
                  type="button"
                  className="usage-statistics__info-btn"
                  onClick={() => onActionNavigate?.(action)}
                  aria-label={translate("v2_actions_list_nav_aria", lang, {
                    name: action.name || action.type || translate("v2_actions_list_this_action", lang),
                  })}
                >
                  <img src={linkIcon} alt="" aria-hidden="true" className="usage-statistics__link-icon" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
